import * as BABYLON from '@babylonjs/core'
import { DisposerBag } from './disposerBag'

export interface WallPhoto {
  url: string
  ratio: number
}

export interface WallPrintSlot {
  /** A point on the wall's surface — its usual x/y/z, e.g. z = depth/2 for a "back" wall at that depth. */
  x: number
  y: number
  z: number
  /** The wall mesh's own rotY at this position (same value `buildRoomShell` uses for that wall). Used to nudge the frame/photo inward and orient the photo to actually face the room. */
  wallRotY: number
  width: number
}

/**
 * The simple "frame box + textured plane, no mat board" print used for wall
 * decor in SpacesStudio and Mountain Lot — duplicated near-identically in
 * both before this. `slots` sit directly on the wall surface; this nudges
 * the frame/photo inward from there and works out the photo plane's facing
 * rotation from the wall's own rotY (a `CreatePlane` faces -Z by default,
 * the opposite of `wallRotY`, since the wall mesh itself is two-sided and
 * doesn't care which way it "faces").
 *
 * PresentRealistic's own `hangPrint` is a fuller recipe (inset mat board,
 * mirror-floor reflection, shadow casting) that's different enough it
 * wasn't worth forcing through this simpler shared version — that one stays
 * bespoke to that screen.
 */
export function hangWallPrints(scene: BABYLON.Scene, bag: DisposerBag, photos: WallPhoto[], slots: WallPrintSlot[]): void {
  if (photos.length === 0 || slots.length === 0) return

  const frameMat = bag.track(new BABYLON.StandardMaterial('wallprint-frame', scene))
  frameMat.diffuseColor = BABYLON.Color3.FromHexString('#0e0c10')
  frameMat.specularColor = BABYLON.Color3.Black()

  slots.forEach((slot, i) => {
    const photo = photos[i % photos.length]
    if (!photo) return
    const h = slot.width / photo.ratio
    const photoRotY = slot.wallRotY + Math.PI
    const inward = new BABYLON.Vector3(Math.sin(slot.wallRotY), 0, Math.cos(slot.wallRotY))

    const frame = bag.track(
      BABYLON.MeshBuilder.CreateBox('wallprint-frame-mesh', { width: slot.width + 0.14, height: h + 0.14, depth: 0.06 }, scene),
    )
    frame.position.set(slot.x, slot.y, slot.z).addInPlace(inward.scale(0.03))
    frame.material = frameMat

    const tex = bag.track(new BABYLON.Texture(photo.url, scene))
    const photoMat = bag.track(new BABYLON.StandardMaterial('wallprint-photo', scene))
    photoMat.diffuseTexture = tex
    photoMat.emissiveTexture = tex
    photoMat.emissiveColor = BABYLON.Color3.FromHexString('#4a4a4a')

    const plane = bag.track(BABYLON.MeshBuilder.CreatePlane('wallprint-photo-mesh', { width: slot.width, height: h }, scene))
    plane.position.set(slot.x, slot.y, slot.z).addInPlace(inward.scale(0.07))
    plane.rotation.y = photoRotY
    plane.material = photoMat
  })
}
